import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import styled from 'styled-components';
import { down } from 'styled-breakpoints';
import snakeize from 'snakeize';

import dollars from '../../utils/dollars';
import { buildHandleEnterKeyPress } from '../../utils/accessibility';

import Button from '../../components/button';
import Cart from '../../components/cart';
import Tooltip from '../../components/tooltip';
import ellipses from '../../components/ellipses.png';

import trackButton from '../../actions/track-button';

import { CART_STEP } from '../../steps';

const convertStep = step => snakeize(step.toUpperCase());
const displayPrice = total => (total ? `${dollars(total / 100)}` : '$--');

class ProgressBarControls extends Component {
  state = {
    showMobileLayout: window.innerWidth <= 616,
    showCart: false,
    showNextTip: true,
  };

  UNSAFE_componentWillMount() {
    window.addEventListener('click', this.hideCart, false);
    window.onresize = this.handleScreenResize;
  }

  componentWillUnmount() {
    window.removeEventListener('click', this.hideCart);
    window.onresize = null;
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    const { showCart: newValue } = nextProps;
    const { showCart: oldValue } = this.props;

    if (!oldValue && newValue) {
      window.removeEventListener('click', this.hideCart);
      this.setState(() => ({ showCart: true }));
    } else if (!newValue) {
      this.setState(() => ({ showCart: false }));
    }
  }

  handleScreenResize = () => {
    const { showMobileLayout } = this.state;

    if (window.innerWidth <= 616 && !showMobileLayout) {
      this.setState(() => ({ showMobileLayout: true }));
    } else if (window.innerWidth > 616 && showMobileLayout) {
      this.setState(() => ({ showMobileLayout: false }));
    }
  };

  hideCart = () => {
    if (this.state.showCart) {
      this.setState(nextState => ({ ...nextState, showCart: false }));
    }
  };

  hideNextTip = () => {
    if (this.state.showNextTip) {
      this.props.trackButton({ name: 'hide_next_tip', step: CART_STEP });
      this.setState(nextState => ({ ...nextState, showNextTip: false }));
    }
  };

  toggleCart = event => {
    if (event && event.stopPropagation) {
      event.stopPropagation();
    }

    this.props.trackButton({ name: 'toggle_cart', step: CART_STEP });

    this.setState(() => ({ showCart: !this.state.showCart }));
  };

  render() {
    const {
      disableAddNewDevice,
      disableNext,
      groupPays,
      nextTip,
      onCompletion,
      step,
      total,
      nextButton,
      requestCost,
      requestDetails,
    } = this.props;
    const { showCart, showMobileLayout, showNextTip } = this.state;

    return (
      <Container
        longNextTip={nextTip && nextTip.length > 40}
        showPrice={!groupPays}
      >
        <div className="tool-tip-container">
          <Button
            disabled={disableNext}
            name={snakeize(step)}
            onClick={onCompletion}
            step={convertStep(step)}
          >
            {nextButton}
          </Button>

          <Tooltip
            active={!!nextTip && showNextTip}
            arrowAlign={'right'}
            onClose={this.hideNextTip}
            position={{ right: '18%' }}
          >
            <p className="next-tip">{nextTip}</p>
          </Tooltip>
        </div>

        <div className="spacer" />

        <div className="tool-tip-container repair-details">
          <div className="price-container">
            <div className="price">
              <p className="price-label">
                {groupPays ? requestDetails : requestCost}
              </p>

              {!groupPays && (
                <p className="price-text">{`${displayPrice(total)}`}</p>
              )}
            </div>

            <div
              className="ellipses-container"
              onClick={this.toggleCart}
              onKeyPress={buildHandleEnterKeyPress(this.toggleCart)}
              tabIndex={0}
            >
              <img alt="ellipses" className="ellipses" src={ellipses} />
              <Tooltip
                active={showCart}
                arrowAlign={showMobileLayout ? 'left' : 'right'}
              >
                <Cart disableAddNewDevice={disableAddNewDevice} />
              </Tooltip>
            </div>
          </div>
        </div>
      </Container>
    );
  }
}

ProgressBarControls.propTypes = {
  disableAddNewDevice: PropTypes.bool,
  disableNext: PropTypes.bool,
  groupPays: PropTypes.bool,
  nextTip: PropTypes.string,
  onCompletion: PropTypes.func,
  showCart: PropTypes.bool,
  step: PropTypes.string.isRequired,
  total: PropTypes.number,
};

ProgressBarControls.defaultProps = {
  disableAddNewDevice: false,
  disableNext: false,
  groupPays: false,
  nextTip: '',
  onCompletion: () => {},
  total: 0,
};

const Container = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;

  ${down('sm')} {
    flex-direction: row-reverse;
    margin-top: 0.9em;
  }

  .tool-tip-container {
    display: flex;
    position: relative;
    width: 100%;
    font-size: 80%;

    .next-tip {
      color: ${props => props.theme.primary};
      display: block;
      font-weight: 500;
      margin: 0;
      padding: 1em 0 1em 1em;
      min-width: 195px;
      overflow: ${props => (props.longNextTip ? 'initial' : 'hidden')};
      white-space: ${props => (props.longNextTip ? 'normal' : 'nowrap')};
    }
  }
  .spacer {
    height: 40px;
    margin: 0 15px;
    background: ${props => props.theme.border};
    width: ${props => (props.showPrice ? `3px` : 'none')};
  }
  .repair-details {
    font-size: unset;
    .price-container {
      display: flex;
      align-items: center;

      ${down('sm')} {
        text-align: right;
        width: 100%;
        justify-content: flex-end;
      }

      .price {
        display: flex;
        flex-direction: column;
        justify-content: space-between calc(85% - 15px);

        .price-label {
          color: ${props => props.theme.subText};
          font-size: 0.8em;
          margin: 0;
          overflow: hidden;
          white-space: nowrap;

          ${down('md')} {
            font-size: 60%;
          }

          ${down('sm')} {
            text-align: right;
          }
        }

        .price-text {
          color: ${props => props.theme.text};
          display: inline-block;
          font-size: 1em;
          margin: 10px 0 0 0;

          ${down('sm')} {
            padding-left: 15px;
          }
        }
      }
      .ellipses-container {
        position: relative;
        height: 20px;
        width: 20px;
        margin: 0 15px 0 20px;
        ${down('sm')} {
          margin: 0 0 0 15px;
        }

        .ellipses {
          cursor: pointer;
          height: 20px;
          width: 20px;
        }
      }
    }
  }
`;

const mapActions = {
  trackButton,
};

export default connect(null, mapActions)(ProgressBarControls);
